<template>
  <v-layout>
    <v-flex>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-container fluid py-0>
            <v-row align="baseline">
              <h1 class="headline">Destinations</h1>
              <v-spacer></v-spacer>
              <div v-if="!treeView">
                <list-filters
                  :filters="filters"
                  label="Find Destination"
                  ref-name="destinationsListFilters"
                  @searchParamsChanged="searchParamsChanged"
                  :expansionPanel="false"
                ></list-filters>
              </div>
              <div v-if="!treeView">
                <list-visible-columns-and-filter
                  :visible-columns="visibleColumns"
                  :selectable-headers="selectableHeaders"
                  v-model="search"
                  @change="onVisibleColumnsChange"
                ></list-visible-columns-and-filter>
              </div>
              <v-switch
                color="primary"
                hide-details
                label="Tree View"
                v-model="treeView"
                @change="treeViewChange"
              ></v-switch>
              <v-tooltip bottom>
                <template v-slot:activator="{ on }">
                  <v-btn
                    class="ml-2"
                    v-on="on"
                    fab
                    dark
                    color="primary"
                    @click="onAdd"
                  >
                    <v-icon dark>mdi-plus</v-icon>
                  </v-btn>
                </template>
                Add New Destination
              </v-tooltip>
            </v-row>
          </v-container>
        </v-col>
      </v-row>
      <treeview-destinations v-if="treeView"></treeview-destinations>
      <template v-else>
        <v-card>
          <v-layout fluid>
            <v-flex xs12>
              <v-data-table
                :headers="filteredHeaders"
                :items="items"
                :loading="$apollo.loading"
                v-bind="pagingOptions"
                item-key="node.id"
                :search="search"
                class="elevation-1"
                sort-by="node.name"
                show-select
                v-model="selected"
              >
                <template v-slot:item.actions="{ item }">
                  <default-list-actions
                    :item="item"
                    :hide-view="true"
                    @deleteItem="onDelete"
                    @editItem="onEdit"
                  ></default-list-actions>
                </template>
                <template v-slot:item.notInUse="{ item }">
                  <v-simple-checkbox
                    v-model="item.node.notInUse"
                    disabled
                  ></v-simple-checkbox>
                </template>
                <template v-slot:footer>
                  <list-actions
                    v-if="selected.length > 0"
                    :selected="selected"
                    list="destinations"
                    :columns="visibleColumns"
                    :filter="searchParams"
                    @close="selected = []"
                    :hide-unexport="true"
                  ></list-actions>
                  <v-row justify="center" v-if="selected.length > 0">
                    <h3 class="py-2 caption">{{ selected.length }} record(s) selected</h3>
                  </v-row>
                </template>
              </v-data-table>
            </v-flex>
          </v-layout>
        </v-card>
        <get-more-results
          :first="first"
          @limitChange="onLimitChange"
          :items="destinations"
          :totalItems="totalItems"
          :loading="$apollo.loading"
          :loadMoreEnabled="loadMoreEnabled"
          @loadMore="loadMore('destinations')"
        ></get-more-results>
      </template>
      <destination-edit-dialog
        :show="showEditDialog"
        v-model="editedDestination"
        @close="showEditDialog = false"
        @save="onSave"
      ></destination-edit-dialog>
      <v-row v-if="debug"></v-row>
    </v-flex>
  </v-layout>
</template>
<script>
import { ListMixin } from "../../../../mixins/ListMixin";
import {
  DESTINATIONS_SEARCH_QUERY,
  DESTINATION_CREATE_MUTATION,
  DESTINATION_UPDATE_MUTATION,
  DESTINATION_DELETE_MUTATION,
  ALL_DESTINATIONS_SEARCH_QUERY,
} from "../../../../constants/graphql";
import GetMoreResults from "../../shared/GetMoreResults";
import DefaultListActions from "../../shared/DefaultListActions";
import ListVisibleColumnsAndFilter from "../../shared/ListVisibleColumnsAndFilter";
import TreeviewDestinations from "./TreeviewDestinations";
import DestinationEditDialog from "./DestinationEditDialog";
import gql from "graphql-tag";
import destFilters from "./destination-filters";
import ListFilters from "../../shared/list-filters/ListFilters.vue";
import ListActions from "../../shared/list-actions/ListActions.vue";

export default {
  name: "AllDestinationsList",
  mixins: [ListMixin],
  components: {
    GetMoreResults,
    DefaultListActions,
    ListVisibleColumnsAndFilter,
    TreeviewDestinations,
    DestinationEditDialog,
    ListFilters,
    ListActions,
  },
  props: {
    refNameColumns: {
      type: String,
      default: () => "destinations-list-cols",
    },
  },

  data() {
    return {
      debug: false,
      selected: [],
      treeView: this.$store.getters.localSettings(
        "destinationsTreeView",
        false
      ),
      destinations: {},

      visibleColumns: this.$store.getters.localSettings(this.refNameColumns, [
        "path",
        "name",
        "code",
        "actions",
      ]),
      headers: [
        {
          sortable: true,
          text: "Name",
          value: "node.name",
          name: "name",
        },
        {
          sortable: true,
          text: "Code",
          value: "node.code",
          name: "code",
        },
        {
          sortable: true,
          text: "Path",
          value: "node.path",
          name: "path",
        },
        {
          sortable: true,
          text: "Brand",
          value: "node.brand",
          name: "brand",
        },
        {
          sortable: true,
          text: "Not In Use",
          value: "notInUse",
          name: "notInUse",
        },
        {
          sortable: true,
          text: "External Code",
          value: "node.externalCode",
          name: "externalCode",
        },
        {
          text: "Actions",
          value: "actions",
          sortable: false,
          name: "actions",
          align: "center",
        },
      ],
      showEditDialog: false,
      editedDestination: {
        id: "",
        name: "",
        isDefault: false,
      },
      newDestination: {
        id: "",
        name: "",
        isDefault: false,
      },
    };
  },

  computed: {
    items() {
      return this.destinations && this.destinations.edges
        ? this.destinations.edges
        : [];
    },
    filters() {
      return destFilters.map((f) => {
        f.props.outlined = this.$store.getters.localSettings("outlinedInputs");
        return f;
      });
    },
  },

  methods: {
    searchParamsChangedListener(params) {
      this.first = 50;
      this.searchParams = params;
    },
    treeViewChange(event) {
      this.$store.dispatch("updateLocalSettings", {
        destinationsTreeView: event,
      });
      this.$emit("input", event);
    },
    onAdd() {
      this.editedDestination = { ...this.newDestination };
      this.showEditDialog = true;
    },
    onEdit(payload) {
      this.editedDestination = { ...payload.node };
      this.showEditDialog = true;
    },
    onDelete(payload) {
      this.$apollo
        .mutate({
          mutation: DESTINATION_DELETE_MUTATION,
          variables: {
            id: payload.node.id,
          },
          update: this.updateCache("deleteDestination"),
        })
        .then((result) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Destination removed.",
            color: "success",
          });
        })
        .catch((error) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: error,
            color: "warning",
          });
        });
    },
    onSave(payload) {
      this.showEditDialog = false;
      const mutation = payload.id
        ? DESTINATION_UPDATE_MUTATION
        : DESTINATION_CREATE_MUTATION;
      this.$apollo
        .mutate({
          mutation: mutation,
          variables: { ...payload },
          update: this.updateCache(
            payload.id ? "updateDestination" : "createDestination"
          ),
        })
        .then((result) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: "Destination saved",
            color: "success",
          });
        })
        .catch((error) => {
          this.$store.dispatch("showAppSnackbar", {
            msg: error,
            color: "danger",
          });
        });
    },
    updateCache(operation) {
      return (store, { data }) => {
        // Update the main list query (used by this component)
        const listQuery = {
          query: DESTINATIONS_SEARCH_QUERY,
          variables: {
            after: this.after,
            filter: this.searchParams,
            first: this.first,
          },
        };

        // Try to update the main list query
        try {
          const listData = store.readQuery(listQuery);
          if (operation.indexOf("create") > -1) {
            listData.destinations.edges.push({
              node: data[operation],
              __typename: "DestinationEdge",
            });
          } else if (operation.indexOf("update") > -1) {
            // Update existing destination in the list
            const index = listData.destinations.edges.findIndex(
              (d) => d.node.id === data[operation].id
            );
            if (index !== -1) {
              listData.destinations.edges[index] = {
                node: data[operation],
                __typename: "DestinationEdge",
              };
            }
          } else if (operation.indexOf("delete") > -1) {
            listData.destinations.edges = listData.destinations.edges.filter(
              (d) => d.node.id !== data[operation].id
            );
          }
          store.writeQuery({
            ...listQuery,
            data: { ...listData },
          });
        } catch (error) {
          // Query might not be in cache yet, which is fine
          // Only log if it's not a "query not found" error
          if (!error.message || !error.message.includes("Can't find field")) {
            console.log("Error updating destinations cache:", error);
          }
        }

        // Update ALL_DESTINATIONS_SEARCH_QUERY (used by TreeviewDestinations)
        // This query has first: 10000 hardcoded, so we don't include it in variables
        // Try common variable combinations since we don't know how it was cached
        const allDestinationsQueries = [
          { query: ALL_DESTINATIONS_SEARCH_QUERY }, // No variables
          {
            query: ALL_DESTINATIONS_SEARCH_QUERY,
            variables: { after: null, filter: {} },
          },
          {
            query: ALL_DESTINATIONS_SEARCH_QUERY,
            variables: { after: null, filter: this.searchParams },
          },
        ];

        for (const query of allDestinationsQueries) {
          try {
            const listData = store.readQuery(query);
            if (operation.indexOf("create") > -1) {
              listData.destinations.edges.push({
                node: data[operation],
                __typename: "DestinationEdge",
              });
            } else if (operation.indexOf("update") > -1) {
              // Update existing destination in the list
              const index = listData.destinations.edges.findIndex(
                (d) => d.node.id === data[operation].id
              );
              if (index !== -1) {
                listData.destinations.edges[index] = {
                  node: data[operation],
                  __typename: "DestinationEdge",
                };
              }
            } else if (operation.indexOf("delete") > -1) {
              listData.destinations.edges = listData.destinations.edges.filter(
                (d) => d.node.id !== data[operation].id
              );
            }
            store.writeQuery({
              ...query,
              data: { ...listData },
            });
            // If we successfully updated one, no need to try others
            break;
          } catch (error) {
            // Query not found with these variables, try next combination
            // This is expected if the query wasn't cached with these variables
            continue;
          }
        }
      };
    },
  },

  apollo: {
    destinations: {
      query: DESTINATIONS_SEARCH_QUERY,
      variables() {
        return {
          after: this.after,
          filter: this.searchParams,
          first: this.first,
        };
      },
      result({ data, loader, networkStatus }) {
        this.totalItems = data ? data.destinations.totalCount : 0;
      },
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/gt/admin/destinations/AllDestinationsList.vue